import useFirebaseAnalytics from "@ch/helpers/utils/firebase/hooks/useFirebaseAnalytics";
import { FirebaseEventEnum } from "@ch/helpers/utils/firebase/types";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { triggerHaptics } from "../utils/triggerHaptics";
import useAudioRecorder from "./useAudioRecorder";
import useKemSpeech from "./useKemSpeech";

type SpeechState = "idle" | "recording" | "playing";

const useKemSpeechPage = () => {
  const navigate = useNavigate();
  const { LogEvent } = useFirebaseAnalytics();
  const [speechState, setSpeechState] = useState<SpeechState>("idle");

  const { isRecording, durationMs, audioBlob, start, stop, discardRecording } =
    useAudioRecorder();

  const { sendAudioMessage, responseAudioUrl, isLoading, resetResponse } =
    useKemSpeech();

  // Format duration for display
  const formatDuration = useCallback((ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }, []);

  const formattedDuration = formatDuration(durationMs);
  const formattedDurationLimit = "1:00"; // 1 minute max

  const handleStartRecording = useCallback(async () => {
    triggerHaptics("medium");

    LogEvent(FirebaseEventEnum.KEM_SPEECH_START_RECORDING, {
      timestamp: new Date().toISOString(),
    });

    await start();
    setSpeechState("recording");
  }, [LogEvent, start]);

  const handleCancelRecording = useCallback(() => {
    triggerHaptics("light");
    discardRecording();
    resetResponse();
    setSpeechState("idle");
  }, [discardRecording, resetResponse]);

  // Auto-stop recording at 1 minute
  useEffect(() => {
    const MAX_DURATION_MS = 60000; // 1 minute
    if (isRecording && durationMs >= MAX_DURATION_MS) {
      void stop();
    }
  }, [isRecording, durationMs, stop]);

  const handleSendRecording = useCallback(async () => {
    if (!isRecording && !audioBlob) return;

    triggerHaptics("success");

    const durationSeconds = Math.floor(durationMs / 1000);

    LogEvent(FirebaseEventEnum.KEM_SPEECH_SEND_RECORDING, {
      timestamp: new Date().toISOString(),
      duration_seconds: durationSeconds,
    });

    try {
      let blob = audioBlob;

      // If still recording, stop it first
      if (isRecording) {
        blob = await stop();
      }

      if (!blob) {
        setSpeechState("idle");
        return;
      }

      await sendAudioMessage(blob);
      setSpeechState("playing");
    } catch {
      setSpeechState("idle");
    }
  }, [isRecording, audioBlob, durationMs, LogEvent, sendAudioMessage, stop]);

  const handleBackPress = useCallback(() => {
    triggerHaptics("light");

    if (speechState === "recording") {
      handleCancelRecording();
    } else if (speechState === "playing") {
      resetResponse();
    }

    void navigate("/kem/chat");
  }, [speechState, handleCancelRecording, resetResponse, navigate]);

  // cleanup on unmount
  useEffect(() => {
    return () => {
      if (isRecording) {
        void stop();
      }
      resetResponse();
    };
  }, [isRecording, resetResponse, stop]);

  return {
    speechState,
    isLoading,
    formattedDuration,
    formattedDurationLimit,
    responseAudioUrl,
    handleStartRecording,
    handleCancelRecording,
    handleSendRecording,
    handleBackPress,
  };
};

export default useKemSpeechPage;
